/**
 * MAHI — global auth context.
 * Provides { user, loading, refresh, signOut } to every MAHI page.
 *
 * Optimistic rendering: if a token is present in localStorage AND we have a
 * cached user to render with, loading is false immediately and the
 * authenticated shell paints without waiting for /auth/me.  The background
 * verify then fills in the real user object (or clears it on 401, at which
 * point api.js's interceptor redirects to login).  This eliminates the
 * 200–500ms blank spinner that blocked render while the round-trip completed.
 *
 * A token with no cached user is the one case that still waits, because there
 * is nothing to be optimistic with: rendering a personalized shell needs a
 * user, and treating "don't know yet" as "not signed in" bounced valid
 * sessions to /login.  See the `loading` initialiser below.
 *
 * Safety: no sensitive action is taken before /auth/me resolves — buy URLs,
 * wardrobe writes, and swipes all go through the API which re-validates the
 * JWT on every call.  The optimistic render is purely cosmetic.
 *
 * Guests: a visitor with no token is not "signed out", they are someone who
 * hasn't been given a session yet, so this provider mints an anonymous one
 * rather than routing them at a login screen.  `user.is_guest` is what the
 * rest of the app branches on, and it is the only difference between the two
 * — a guest has a real user_id and every screen except the ones that need a
 * photo or a phone number works exactly the same for them.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import mahi from './api';
import { clearCached } from './cache';
import { startSession } from './analytics';

const Ctx = createContext({ user: null, loading: false, refresh: () => {}, signOut: () => {} });

/**
 * Get a session, one way or another.
 *
 * index.html fires this same request before the bundle has even parsed, so the
 * common path is that a token is already here and this does nothing.  The two
 * slower paths exist because that head start is best-effort: `__mahiGuestBoot`
 * is awaited (not raced, not re-fired) when it is still in flight, because
 * starting a second POST would hand one visitor two accounts and orphan
 * whichever one lost.  Only if the boot script never ran or came back
 * empty-handed do we pay for the round trip here.
 */
async function _ensureSession() {
  try {
    if (localStorage.getItem('mira_token')) return true;
  } catch { return false; }  // storage blocked — nothing we can persist

  if (typeof window !== 'undefined' && window.__mahiGuestBoot) {
    try {
      const booted = await window.__mahiGuestBoot;
      if (booted?.token) return true;
    } catch { /* fall through and mint one ourselves */ }
  }

  try {
    const { data } = await mahi.post('/auth/guest');
    if (!data?.token) return false;
    localStorage.setItem('mira_token', data.token);
    if (data.user) localStorage.setItem('mahi_user_cache', JSON.stringify(data.user));
    return true;
  } catch {
    // Rate-limited, offline, or the backend is down.  Returning false leaves
    // `user` null, which the router treats as "show the signed-out door"
    // rather than looping on a request that is already failing.
    return false;
  }
}

// Lightweight cached user — only the fields the shell UI needs before /auth/me
// resolves (name, avatar, gender_hint, etc.).  Falls back to null so
// components that need the full user object must still guard against it.
function _getCachedUser() {
  try {
    const raw = localStorage.getItem('mahi_user_cache');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function _setCachedUser(user) {
  try {
    if (user) localStorage.setItem('mahi_user_cache', JSON.stringify(user));
    else localStorage.removeItem('mahi_user_cache');
  } catch { /* storage full or private mode — silently ignore */ }
}

export const MahiAuthProvider = ({ children }) => {
  const hasToken = Boolean(localStorage.getItem('mira_token'));

  // If a token exists, start with the cached user (may be null on first visit).
  // /auth/me runs in the background and updates user state once it resolves.
  // Read once and reused below — `loading` depends on the same answer, and two
  // reads could disagree if storage were cleared between them.
  const cachedUser = hasToken ? _getCachedUser() : null;
  const [user, setUser] = useState(cachedUser);

  // True only while there is genuinely nothing to render: a token in hand but
  // no cached user to be optimistic *with*. That is the one case the flag
  // exists for, and it is deliberately not "is /auth/me in flight" — with a
  // cached user the shell paints immediately and the verify happens behind it,
  // which is the whole point of the optimistic path above.
  //
  // It was previously hardcoded false, which read as a stronger version of the
  // same idea but broke both branches in MahiApp's `Guarded` that depend on
  // it. A cold load with a valid token and an empty user cache — a first visit
  // on a new device, or any sign-in that predates this cache — fell straight
  // through to `!user` and bounced to /login before /auth/me had even
  // answered. It also left the /discover intro handover (IntroFallback) as
  // dead code, so the boot wordmark blinked out and back instead of being one
  // continuous mark from first paint until the letters move (see MahiIntro).
  //
  // One-shot: set false when the first verify settles and never true again, so
  // a later refresh() (say, after a photo change) can't blank a live screen.
  //
  // No token now also means waiting, where it used to mean "signed out": a
  // guest session is being minted, and rendering the signed-out door for the
  // 100–200ms that takes would flash a login screen at someone who is about
  // to be let straight in.
  const [loading, setLoading] = useState(!cachedUser);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('mira_token')) {
      // No session yet — mint an anonymous one rather than treating this
      // visitor as signed out. Only a genuine failure to get one (offline,
      // rate-limited, backend down) falls through to the null-user path.
      const ok = await _ensureSession();
      if (!ok) {
        setUser(null);
        _setCachedUser(null);
        setLoading(false);
        return null;
      }
    }
    // One session per authenticated tab — see analytics.js's docstring for
    // why this, not a request interceptor or a page-level effect, is the
    // single place session.start/heartbeat gets attached app-wide.
    startSession();
    try {
      const { data } = await mahi.get('/auth/me');
      setUser(data.user);
      _setCachedUser(data.user);
      return data.user;
    } catch {
      // 401 → api.js interceptor clears token and redirects to login.
      // Any other error (network timeout, 5xx): keep the optimistic cached
      // user in place — don't sign out the user on a transient failure.
      return null;
    } finally {
      // Whatever the answer was, the question has been asked. On failure with
      // no cached user this hands `Guarded` a null user and it routes to
      // login — the same destination as before, now reached by having actually
      // asked rather than by assuming.
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('mira_token');
    _setCachedUser(null);
    // The wardrobe and chat-history caches are keyed by user id, so they could
    // not be served to whoever signs in next — but leaving one person's closet
    // sitting in storage on a shared device is not something to rely on a key
    // scheme to excuse.
    clearCached();
    setUser(null);
    window.location.href = '/login';
  }, []);

  // Background verify — fires after first render, never blocks it.
  useEffect(() => { refresh(); }, [refresh]);

  return <Ctx.Provider value={{ user, loading, refresh, signOut }}>{children}</Ctx.Provider>;
};

export const useMahiAuth = () => useContext(Ctx);
